import React, { useState, useEffect } from 'react';
import { RoutingRule } from '../../../types/qr.types';
import { RuleConditionEditor } from './RuleConditionEditor';
import { RuleActionEditor } from './RuleActionEditor';
import { Input } from '../../Input';

interface RuleCardProps {
  rule: RoutingRule;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  onChange: (rule: RoutingRule) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

interface RuleCardProps {
  rule: RoutingRule;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  onChange: (rule: RoutingRule) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  defaultExpanded?: boolean; // New prop to control default expansion state
  onToggleExpand?: () => void; // Optional callback for external expand/collapse control
  hasGeoRoutingFeature?: boolean; // Whether user has geographic routing feature
}

export function RuleCard({
  rule,
  index,
  isFirst,
  isLast,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  defaultExpanded = true, // Default to expanded for backwards compatibility
  onToggleExpand,
  hasGeoRoutingFeature = false,
}: RuleCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Sync with prop changes
  React.useEffect(() => {
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    onToggleExpand?.();
  };

  const getConditionsSummary = () => {
    if (rule.conditions.items.length === 0) {
      return 'Always matches';
    }
    const types = rule.conditions.items.map(c => {
      switch (c.type) {
        case 'time': return 'Time';
        case 'geographic': return 'Location';
        case 'device': return 'Device';
      }
    });
    return types.join(` ${rule.conditions.operator} `);
  };

  const getActionSummary = () => {
    if (rule.action.type === 'redirect') {
      return rule.action.url || 'No URL set';
    }
    if (rule.action.type === 'image') {
      return rule.action.image_id ? 'Show image' : 'No image selected';
    }
    return `A/B Test (${rule.action.urls.length} variants)`;
  };

  return (
    <div className={`border rounded-lg bg-white shadow-sm ${!rule.enabled ? 'opacity-60' : ''}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-100">
        {/* Drag handle / Priority indicator */}
        <div className="flex flex-col items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className={`p-1 rounded hover:bg-gray-100 ${isFirst ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500'}`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <span className="text-xs font-medium text-gray-400">#{index + 1}</span>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className={`p-1 rounded hover:bg-gray-100 ${isLast ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500'}`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Rule info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              index === 0 ? 'bg-[--clinqr-primary] text-white' : 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary]'
            }`}>
              {index === 0 ? 'IF' : 'ELSE IF'}
            </span>
            <input
              type="text"
              value={rule.name}
              onChange={(e) => onChange({ ...rule, name: e.target.value })}
              className="font-medium text-gray-900 bg-transparent border-0 focus:outline-none focus:ring-0 p-0 flex-1 hover:bg-gray-50 focus:bg-gray-50 rounded px-1 py-0.5 transition-colors cursor-text"
              placeholder="Rule name"
              title="Click to edit rule name"
            />
          </div>
          {!isExpanded && (
            <p className="text-sm text-gray-500 mt-1 truncate">
              {getConditionsSummary()} → {getActionSummary()}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={rule.enabled}
              onChange={(e) => onChange({ ...rule, enabled: e.target.checked })}
              className="rounded border-[--clinqr-border]/20 text-[--clinqr-primary] focus:ring-[--clinqr-primary]"
            />
            <span className="text-sm text-[--clinqr-text-secondary]">Enabled</span>
          </label>

          <button
            type="button"
            onClick={handleToggle}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <svg
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <button
            type="button"
            onClick={onDelete}
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          <RuleConditionEditor
            conditions={rule.conditions}
            onChange={(conditions) => onChange({ ...rule, conditions })}
            hasGeoRoutingFeature={hasGeoRoutingFeature}
          />

          <div className="border-t border-gray-200 pt-4">
            <RuleActionEditor
              action={rule.action}
              onChange={(action) => onChange({ ...rule, action })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
